"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"
import { Mention } from "@/schema/cross_reference"
import { BaseInlineInput } from "@ui/forms/inline/base"
import { InlineInputProps } from "@ui/forms/inline/props"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { Plus, X } from "lucide-react"

import { useGlobalSearch } from "@/hooks/use-global-search"
import { MentionDropdown } from "@/components/lexical/plugins/mention-dropdown/mention-dropdown"
import { MentionOption } from "@/components/lexical/plugins/mention-dropdown/types"
import PeekView from "@/components/peek-view"

interface InlineMentionEditorProps extends Pick<InlineInputProps, "fixedLabelWidth" | "LabelIcon"> {
  mentions: Mention[]
  onChange?: (refs: Mention[]) => void
  disabled?: boolean
  bookId?: string
}

const ALLOWED_REFERENCE_TYPES = ["client", "prospect", "person", "business", "household"] as const

export function InlineMentionEditor({
  mentions,
  onChange,
  fixedLabelWidth,
  LabelIcon,
  disabled = false,
  bookId,
}: InlineMentionEditorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { results, handleSearchChange, isSearching, searchQuery } = useGlobalSearch({
    allowedTypes: ALLOWED_REFERENCE_TYPES,
    bookIdOverride: bookId,
  })

  const mentionOptions: MentionOption[] = results.map((r) => new MentionOption(r))

  useEffect(() => {
    setSelectedIndex(0)
  }, [results])

  const handleSelect = useCallback(
    (option: MentionOption) => {
      if (!onChange) return

      const newReference: Mention = {
        target_id: option.recordId,
        target_type: option.recordType,
        target_label: option.label,
      }

      const exists = mentions.some(
        (r) => r.target_id === newReference.target_id && r.target_type === newReference.target_type
      )
      if (!exists) {
        onChange([...mentions, newReference])
      }

      setIsOpen(false)
      handleSearchChange("")
    },
    [mentions, onChange, handleSearchChange]
  )

  const handleRemove = useCallback(
    (reference: Mention) => {
      if (!onChange) return
      onChange(
        mentions.filter((r) => !(r.target_id === reference.target_id && r.target_type === reference.target_type))
      )
    },
    [mentions, onChange]
  )

  const SUPPORTED_PEEK_TYPES = ["Client", "Prospect", "Household", "Person", "Business"] as const
  type PeekType = (typeof SUPPORTED_PEEK_TYPES)[number]

  const getPeekViewType = (type: string): PeekType | null =>
    SUPPORTED_PEEK_TYPES.includes(type as PeekType) ? (type as PeekType) : null

  return (
    <BaseInlineInput label="Related records" LabelIcon={LabelIcon} fixedLabelWidth={fixedLabelWidth}>
      <div className="flex w-full flex-col gap-2">
        {mentions.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2">
            {mentions.map((reference) => {
              const peekType = getPeekViewType(reference.target_type)
              return (
                <div
                  key={`${reference.target_type}-${reference.target_id}`}
                  className="group inline-flex items-center gap-1"
                >
                  {peekType ? (
                    <PeekView
                      recordType={peekType}
                      recordId={reference.target_id}
                      label={reference.target_label}
                      size="sm"
                    />
                  ) : (
                    <span className="text-sm">{reference.target_label}</span>
                  )}
                  {!disabled && (
                    <button
                      onClick={() => handleRemove(reference)}
                      className="text-muted-foreground hover:text-foreground p-0.5"
                      type="button"
                      aria-label={`Remove ${reference.target_label}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {!disabled && (
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-primary flex h-8 w-fit cursor-pointer items-center gap-1.5 rounded-md px-2 text-sm focus:outline-1"
              >
                <Plus className="size-3.5" />
                Add record
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-64 p-0"
              align="start"
              onOpenAutoFocus={(e) => {
                e.preventDefault()
                searchInputRef.current?.focus()
              }}
            >
              <MentionDropdown
                options={mentionOptions}
                selectedIndex={selectedIndex}
                searching={isSearching}
                searchQuery={searchQuery}
                onSelect={handleSelect}
                onSearchChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault()
                    setSelectedIndex((prev) => Math.min(prev + 1, mentionOptions.length - 1))
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault()
                    setSelectedIndex((prev) => Math.max(prev - 1, 0))
                  } else if (e.key === "Enter" && mentionOptions.length > 0) {
                    e.preventDefault()
                    handleSelect(mentionOptions[selectedIndex])
                  } else if (e.key === "Escape") {
                    setIsOpen(false)
                  }
                }}
                searchInputRef={searchInputRef}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    </BaseInlineInput>
  )
}
